import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from '../../../../core/api/api.service';
import { API_HOST } from '../../../../core/api/api.config';
import { ENDPOINTS } from '../../../../core/api/endpoints';

export interface LeadStatusActionPayload {
  entySeq: number;
  entyType: number;
  cmmnt: string;
}

@Injectable({
  providedIn: 'root',
})
export class LeadService {
  private leadFormData: any = {};
  private leadSeq: number | null = null;
  private isEditMode = false;

  private leadSeqSubject = new BehaviorSubject<number | null>(null);
  leadSeq$ = this.leadSeqSubject.asObservable();

  setLeadForm(data: any) {
    this.leadFormData = data;
  }

  saveOnboardingReview(payload: { entySeq: number; cmmnt: string; entyType: any; clntSeq: any }): Observable<any> {
    return this.api.post(API_HOST.onboarding, ENDPOINTS.ONBOARDING.SAVE_ONBORDING, payload);
  }

  getOnboardingReview(leadSeq: number, modSeq: string): Observable<any> {
    return this.api.get(
      API_HOST.onboarding,
      `${ENDPOINTS.ONBOARDING.GET_ONBOARDING_REVIEW}/${leadSeq}/${modSeq}`,
    );
  }

  getLeadForm() {
    return this.leadFormData;
  }

  setLeadSeq(seq: number | null) {
    this.leadSeq = seq;
    this.isEditMode = !!seq;
    this.leadSeqSubject.next(seq); // 🔥 notify all components
  }

  getLeadSeq() {
    return this.leadSeq;
  }

  getEditMode() {
    return this.isEditMode;
  }

  constructor(private api: ApiService) {}

  /* ================= CREATE / UPDATE ================= */

  saveLead(payload: any): Observable<any> {
    return this.api.post(API_HOST.leads, ENDPOINTS.LEADS.SAVE_LEAD, payload);
  }

  saveTermSheet(payload: any): Observable<any> {
    return this.api.post(API_HOST.leads, ENDPOINTS.LEADS.SAVE_TERM_SHEET, payload);
  }

  saveLeadDocs(payload: any): Observable<any> {
    return this.api.post(API_HOST.leads, ENDPOINTS.LEADS.UPLOAD_LEAD_DOCS, payload);
  }

  /**
   * Status update ab entySeq, entyType aur cmmnt ek hi body mein bhejta hai.
   * Comment ke liye alag call ki zaroorat nahi.
   */
  updateLeadStatus(payload: LeadStatusActionPayload): Observable<any> {
    return this.api.post(API_HOST.leads, ENDPOINTS.LEADS.UPDATE_STATUS, payload);
  }

  /* ================= GET ================= */

  getLeadList(): Observable<any> {
    return this.api.get(API_HOST.leads, ENDPOINTS.LEADS.GET_LEAD_LIST);
  }

  getLeadById(leadSeq: number): Observable<any> {
    return this.api.get(API_HOST.leads, ENDPOINTS.LEADS.GET_LEAD + leadSeq);
  }

  getLeadDocs(leadSeq: number, entyTyp: number): Observable<any> {
    return this.api.get(API_HOST.leads, `${ENDPOINTS.LEADS.GET_LEAD_DOCS}${leadSeq}/${entyTyp}`);
  }

  getClientApprovalLeads(): Observable<any> {
    return this.api.get(API_HOST.leads, ENDPOINTS.LEADS.GET_CLIENT_APPROVAL_LIST);
  }

  getAllLeadsCount(): Observable<any> {
    return this.api.get(API_HOST.leads, ENDPOINTS.LEADS.GET_ALL_LEADS_COUNT);
  }

  getAllSTPLeadsDocs(ctgryKey: number): Observable<any> {
    return this.api.get(API_HOST.leads, `${ENDPOINTS.LEADS.GET_ALL_STP_LEADS_DOCS}/${ctgryKey}`);
  }

  submitLead(leadSeq: number, modSeq: number): Observable<any> {
    return this.api.post(API_HOST.leads, `${ENDPOINTS.LEADS.SUBMIT_LEAD}${leadSeq}/${modSeq}`, {});
  }

  getLeadTerm(trmSeq: number): Observable<any> {
    return this.api.get(API_HOST.leads, ENDPOINTS.LEADS.GET_LEAD_TERM + trmSeq);
  }

  getLeadTermList(leadSeq: number): Observable<any> {
    return this.api.get(API_HOST.leads, ENDPOINTS.LEADS.GET_LEAD_TERM_LIST + leadSeq);
  }

  getLeadDocHistory(leadSeq: number, entyTyp: number, docType: number): Observable<any> {
    const base = ENDPOINTS.LEADS.GET_LEAD_DOC_HISTORY.replace(/\/$/, '');
    return this.api.get(API_HOST.leads, `${base}/${leadSeq}/${entyTyp}/${docType}`);
  }

  getClntAprvlLeads() {
    return this.api.get(API_HOST.leads, ENDPOINTS.LEADS.GET_CLIENT_APPROVAL_LIST);
  }

  /**
   * Approve lead ka endpoint sirf leadSeq path param leta hai
   * (/leads/apprv-lead/{leadSeq}) — koi body payload nahi.
   * Isliye comment ab bhi saveOnboardingReview se alag save hota hai.
   */
 leadApproved(
  payload: LeadStatusActionPayload,
): Observable<any> {
  return this.api.post(
    API_HOST.leads,
    ENDPOINTS.LEADS.APPROVE_LEAD,
    payload,
  );
}

  /**
   * Reject lead ab entySeq, entyType aur cmmnt same body mein bhejta hai.
   */
  rejectLead(payload: LeadStatusActionPayload): Observable<any> {
    return this.api.post(API_HOST.leads, ENDPOINTS.LEADS.REJECT_LEAD, payload);
  }

  /**
   * Internal reject bhi same tarah entySeq, entyType, cmmnt ek call mein.
   */
  INTERNAL_REJECT(payload: LeadStatusActionPayload): Observable<any> {
    return this.api.post(API_HOST.leads, ENDPOINTS.LEADS.REJECT_LEAD_INTRNL, payload);
  }

  emailLeadExtractionDirect(emailText: string): Observable<any> {
    return this.api.post('', 'http://165.99.50.189:8000/email/lead-extraction', { emailText });
  }

  /**
   * Send back to RO bhi entySeq, entyType, cmmnt same body mein bhejta hai.
   */
  sendLeadBackToRO(payload: LeadStatusActionPayload): Observable<any> {
    return this.api.post(API_HOST.leads, ENDPOINTS.LEADS.SEND_BACK_TO_RO, payload);
  }
}